import LocalLink from "@/components/LocalLink";
import { useLocale } from "@/lib/i18n/useLocale";
import { localise, type Locale } from "@/lib/i18n/registry";
import type { Block, QA } from "./types";
import { ScrollReveal } from "@/components/ScrollReveal";

/**
 * Рендерер блочных страниц (BTQ-47, Этап 1).
 *
 * Страница, у которой композиция на португальском отличается от английской,
 * описывается списком блоков (см. types.ts) и рендерится здесь. Английская
 * сторона продолжает жить своими компонентами — её мы не трогаем.
 *
 * Задача этого файла — СЕМАНТИКА и извлекаемость: правильная иерархия
 * заголовков, настоящие <table> для цен, <ol> для шагов, FAQ парами. Именно
 * это читают AI Overview и Modo IA. Визуальная отделка под фирменный стиль
 * доводится вместе с первой волной контента, когда есть что отделывать.
 */


/**
 * Ссылка по ключу реестра, которая НИКОГДА не уводит на другой язык.
 * Если живой португальской цели нет и замена не объявлена — рисуется просто
 * текст без ссылки: подпись остаётся на месте, а посетитель не выпадает из
 * португальской версии (BTQ-47).
 */
function RouteLink({ routeKey, locale, className, children }: {
  routeKey: string; locale: Locale; className?: string; children: React.ReactNode;
}) {
  const { href, fellBack } = localise(routeKey, locale);
  if (!href) return <>{children}</>;
  return (
    <LocalLink to={href} className={className} data-i18n-substitute={fellBack ? "" : undefined}>
      {children}
    </LocalLink>
  );
}

const CLS = {
  section: "mb-14 md:mb-20",
  h2: "text-editorial text-heading mb-5",
  h3: "text-editorial text-subheading mb-3",
  p: "text-foreground/80 leading-relaxed mb-4 max-w-[68ch]",
  /** Сводный ответ намеренно крупнее: это первое, что читают и человек, и машина. */
  answer: "text-[17px] md:text-[19px] leading-relaxed text-foreground mb-4 max-w-[62ch]",
  tableWrap: "overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0",
  table: "w-full text-[15px] border-collapse",
  th: "text-left font-medium py-3 pr-6 border-b border-foreground/20 whitespace-nowrap",
  td: "py-3 pr-6 border-b border-foreground/10 align-top",
  note: "text-[13px] text-foreground/60 mt-3",
  card: "border border-foreground/10 p-5",
  dl: "space-y-6",
  cta: "inline-flex items-center bg-foreground text-primary-foreground px-10 py-4 text-caption",
};

function Paragraphs({ items, className = CLS.p }: { items: string[]; className?: string }) {
  return <>{items.map((t, i) => <p key={i} className={className}>{t}</p>)}</>;
}

function QAList({ items }: { items: QA[] }) {
  // <dl> вместо аккордеона: ответ должен быть в разметке всегда, а не после
  // клика — скрытый за интеракцией текст машины не извлекают.
  return (
    <dl className={CLS.dl}>
      {items.map((qa, i) => (
        <div key={i}>
          <dt className="font-medium text-foreground mb-2">{qa.q}</dt>
          <dd className="text-foreground/80 leading-relaxed max-w-[68ch]">{qa.a}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Поле карточки. Пустое значение печатается как «não publicado», а не
 * скрывается: пробел, названный пробелом, — это тоже сигнал доверия, а
 * молчаливое отсутствие строки читается как «у них этого нет».
 * Час окончания музыки и обязательные подрядчики — ровно те поля, из-за
 * которых на эту страницу приходят, и врать в них дороже всего.
 */
/**
 * Изображения идут через `/img` собственного домена, а не напрямую из хранилища:
 * прямые ссылки на бакет однажды выели 312% квоты за цикл (BTQ-67). Маршрут
 * проксирует в render-эндпоинт и кэшируется на границе Cloudflare.
 */
const img = (path: string, w = 1600, q = 84) =>
  `https://eventboutique.pt/img/${path}?width=${w}&quality=${q}`;

/**
 * Полноэкранный герой — та же мебель, что у английских страниц: фотография во
 * всю ширину, затемнение, светлый текст, `data-header-theme="hero"` для шапки.
 * Без него португальская страница читается как текстовый документ рядом с
 * кинематографичной английской, а это и есть провал зеркала.
 */
function HeroWithImage({ eyebrow, h1, lede, image, imageAlt }: {
  eyebrow?: string; h1: string; lede?: string; image: string; imageAlt?: string;
}) {
  return (
    <section className="relative min-h-[72vh] md:min-h-[85vh] flex items-end overflow-hidden" data-header-theme="hero">
      <div className="absolute inset-0 bg-[hsl(0,0%,8%)]" />
      <div className="absolute inset-0">
        <img
          src={img(image, 1600, 86)}
          srcSet={`${img(image, 768, 80)} 768w, ${img(image, 1280, 84)} 1280w, ${img(image, 1600, 86)} 1600w, ${img(image, 1920, 88)} 1920w`}
          sizes="100vw"
          alt={imageAlt ?? ""}
          className="w-full h-full object-cover object-center"
          width={1920}
          height={1080}
          fetchPriority="high"
          loading="eager"
          decoding="sync"
          style={{ filter: "saturate(0.3) brightness(0.4)" }}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />
      <div className="relative z-10 luxury-container pb-16 md:pb-28 pt-40">
        {eyebrow && <p className="text-caption text-white/70 mb-4">{eyebrow}</p>}
        <h1 className="text-editorial text-[clamp(2.4rem,5.5vw,4.5rem)] leading-[1] text-white tracking-[0.05em] max-w-4xl">
          {h1}
        </h1>
        {lede && <p className="mt-6 text-white/80 text-[17px] md:text-[19px] leading-relaxed max-w-[62ch]">{lede}</p>}
      </div>
    </section>
  );
}

function CardField({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <dt className="inline text-foreground/60">{label}: </dt>
      <dd className="inline">{value ?? <span className="text-foreground/40">não publicado</span>}</dd>
    </div>
  );
}

export function Blocks({ blocks }: { blocks: Block[] }) {
  const locale = useLocale();

  return (
    <>
      {blocks.map((b, i) => {
        // Полноэкранный герой подаёт себя сам; остальные блоки появляются на
        // скролле тем же компонентом, что и секции английских страниц, — иначе
        // португальская версия статична там, где английская живая.
        if (b.type === "hero" && b.image) {
          return <HeroWithImage key={i} eyebrow={b.eyebrow} h1={b.h1} lede={b.lede} image={b.image} imageAlt={b.imageAlt} />;
        }
        const node = (() => {
        switch (b.type) {
          case "hero":
            return (
              <header key={i} className={CLS.section}>
                {b.eyebrow && <p className="text-caption text-foreground/60 mb-4">{b.eyebrow}</p>}
                <h1 className="text-editorial text-display mb-4">{b.h1}</h1>
                {b.lede && <p className={CLS.answer}>{b.lede}</p>}
              </header>
            );

          case "answerFirst":
            // data-answer-first — метка для проверки на билде: сводный ответ
            // обязан стоять выше таблиц и содержать число.
            return (
              <section key={i} className={CLS.section} data-answer-first="">
                <Paragraphs items={b.paragraphs} className={CLS.answer} />
              </section>
            );

          case "table":
            return (
              <section key={i} className={CLS.section}>
                {b.heading && <h2 className={CLS.h2}>{b.heading}</h2>}
                <div className={CLS.tableWrap}>
                  <table className={CLS.table}>
                    <thead>
                      <tr>{b.columns.map((c, j) => <th key={j} scope="col" className={CLS.th}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {b.rows.map((row, j) => (
                        <tr key={j}>{row.map((cell, k) => <td key={k} className={CLS.td}>{cell}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {b.note && <p className={CLS.note}>{b.note}</p>}
              </section>
            );

          case "steps":
            return (
              <section key={i} className={CLS.section}>
                <h2 className={CLS.h2}>{b.heading}</h2>
                {b.intro && <p className={CLS.p}>{b.intro}</p>}
                <ol className="space-y-8">
                  {b.steps.map((s, j) => (
                    <li key={j}>
                      <p className="text-caption text-foreground/60 mb-1">{s.when}</p>
                      <h3 className={CLS.h3}>{s.title}</h3>
                      <Paragraphs items={s.body} />
                      {s.money && <p className="text-[15px] text-foreground mb-2"><strong>{s.money}</strong></p>}
                      {s.risk && <p className={CLS.note}>{s.risk}</p>}
                    </li>
                  ))}
                </ol>
              </section>
            );

          case "catalog":
            return (
              <section key={i} className={CLS.section}>
                <h2 className={CLS.h2}>{b.heading}</h2>
                {b.intro && <p className={CLS.p}>{b.intro}</p>}
                {b.groups.map((g, j) => (
                  <div key={j} className="mb-10">
                    <h3 className={CLS.h3}>{g.geo}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {g.cards.map((c, k) => (
                        <article key={k} className={CLS.card}>
                          <h4 className="font-medium mb-1">
                            {c.routeKey
                              ? <RouteLink routeKey={c.routeKey} locale={locale} className="underline underline-offset-4">{c.name}</RouteLink>
                              : c.name}
                          </h4>
                          <p className="text-[13px] text-foreground/60 mb-3">{c.municipality}</p>
                          <dl className="text-[14px] space-y-1">
                            <CardField label="Sentados, interior" value={c.seatedIndoor} />
                            <CardField label="Sentados, exterior" value={c.seatedOutdoor} />
                            <CardField label="Cozinha" value={c.kitchen} />
                            <CardField label="Alojamento" value={c.roomsNote ?? (c.rooms != null ? `${c.rooms} quartos` : undefined)} />
                            <CardField label="Preço" value={c.price} />
                            <CardField label="Plano para chuva" value={c.rainPlan} />
                            <CardField label="Música até" value={c.musicUntil} />
                            <CardField label="Fornecedores obrigatórios" value={c.suppliers} />
                          </dl>
                        </article>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            );

          case "questions":
          case "faq":
            return (
              <section key={i} className={CLS.section} data-block={b.type}>
                <h2 className={CLS.h2}>{b.heading}</h2>
                {"intro" in b && b.intro && <p className={CLS.p}>{b.intro}</p>}
                <QAList items={b.items} />
              </section>
            );

          case "procedural":
            return (
              <section key={i} className={CLS.section} data-block="procedural">
                <h2 className={CLS.h2}>{b.heading}</h2>
                {b.intro && <p className={CLS.p}>{b.intro}</p>}
                <ul className="space-y-3 max-w-[68ch]">
                  {b.items.map((t, j) => (
                    <li key={j} className="text-foreground/80 leading-relaxed pl-5 relative">
                      <span className="absolute left-0 top-[0.7em] w-2 h-px bg-foreground/40" />{t}
                    </li>
                  ))}
                </ul>
                {b.note && <p className={CLS.note}>{b.note}</p>}
              </section>
            );

          case "prose":
            return (
              <section key={i} className={CLS.section}>
                {b.heading && <h2 className={CLS.h2}>{b.heading}</h2>}
                <Paragraphs items={b.paragraphs} />
              </section>
            );

          case "audience":
            return (
              <section key={i} className={CLS.section}>
                <h2 className={CLS.h2}>{b.heading}</h2>
                <ul className="space-y-3 max-w-[68ch]">
                  {b.items.map((t, j) => <li key={j} className="text-foreground/80 leading-relaxed">{t}</li>)}
                </ul>
              </section>
            );

          case "related":
            return (
              <nav key={i} className={CLS.section} aria-label={b.heading}>
                <h2 className={CLS.h2}>{b.heading}</h2>
                <ul className="space-y-2">
                  {b.links.map((l, j) => (
                    <li key={j}>
                      <RouteLink routeKey={l.routeKey} locale={locale} className="underline underline-offset-4">{l.label}</RouteLink>
                    </li>
                  ))}
                </ul>
              </nav>
            );

          case "cta":
            return (
              <section key={i} className={CLS.section}>
                <h2 className={CLS.h2}>{b.heading}</h2>
                {b.body && <p className={CLS.p}>{b.body}</p>}
                <RouteLink routeKey={b.routeKey} locale={locale} className={CLS.cta}>{b.label}</RouteLink>
              </section>
            );

          default: {
            // Исчерпывающая проверка: новый тип блока без ветки здесь не соберётся.
            const never: never = b;
            return never;
          }
        }
        })();
        return <ScrollReveal key={i}>{node}</ScrollReveal>;
      })}
    </>
  );
}

/**
 * JSON-LD прямо из блоков — разметка не может разойтись с видимым текстом,
 * потому что источник у них один. В v1 они велись раздельно, и на EB-стороне
 * граф ссылался на узел Organization, которого на странице не было.
 */
export function schemaFromBlocks(blocks: Block[], orgId: string): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const faq = blocks.find((b) => b.type === "faq");
  if (faq && faq.type === "faq" && faq.items.length) {
    out.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      about: { "@id": orgId },
      mainEntity: faq.items.map((qa) => ({
        "@type": "Question",
        name: qa.q,
        acceptedAnswer: { "@type": "Answer", text: qa.a },
      })),
    });
  }
  const steps = blocks.find((b) => b.type === "steps");
  if (steps && steps.type === "steps" && steps.steps.length) {
    out.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: steps.heading,
      step: steps.steps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.title,
        text: s.body.join(" "),
      })),
    });
  }
  return out;
}
